import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import ReactECharts from 'echarts-for-react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import {
  ArrowLeft, Upload, FileText, BarChart3, BookOpen,
  CheckCircle, AlertCircle, Loader2, Sparkles, Brain,
  Microscope, Target, Beaker, LineChart, PieChart,
  Radar, Activity, Globe, Wind, CloudRain, Thermometer,
  ChevronDown, ChevronUp, Copy, Eye, X, Zap, Sigma,
  Quote, Lightbulb, FileCheck, Layers
} from 'lucide-react'
import { API_BASE } from '../App'

// 论文语言检测
function detectLanguage(text) {
  const chineseChars = (text.match(/[\u4e00-\u9fff]/g) || []).length
  const totalChars = text.replace(/\s/g, '').length
  if (totalChars === 0) return 'en'
  const chineseRatio = chineseChars / totalChars
  return chineseRatio > 0.3 ? 'zh' : 'en'
}

// 配置 marked：支持 GitHub 风格 Markdown
marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: false,
  mangle: false
})

// 渲染 Markdown 文本（含 KaTeX 公式支持）
function renderMarkdownWithKatex(text) {
  if (!text) return ''
  // 先把 \(...\) 和 \[...\] 转换为占位符，避免被 Markdown 转义
  const placeholders = []
  let protected_ = text

  // 保护行内公式 \(...\)
  protected_ = protected_.replace(/\\\(([\s\S]+?)\\\)/g, (m, formula) => {
    const idx = placeholders.length
    placeholders.push({ type: 'inline', formula: formula.trim() })
    return `@@KATEX_INLINE_${idx}@@`
  })
  // 保护块级公式 \[...\]
  protected_ = protected_.replace(/\\\[([\s\S]+?)\\\]/g, (m, formula) => {
    const idx = placeholders.length
    placeholders.push({ type: 'block', formula: formula.trim() })
    return `@@KATEX_BLOCK_${idx}@@`
  })
  // 保护 $...$ 行内公式（避免与 Markdown 冲突）
  protected_ = protected_.replace(/(?<![\\$])\$([^\$\n]+?)\$(?![\\$])/g, (m, formula) => {
    const idx = placeholders.length
    placeholders.push({ type: 'inline', formula: formula.trim() })
    return `@@KATEX_INLINE_${idx}@@`
  })
  // 保护 $$...$$ 块级公式
  protected_ = protected_.replace(/\$\$([\s\S]+?)\$\$/g, (m, formula) => {
    const idx = placeholders.length
    placeholders.push({ type: 'block', formula: formula.trim() })
    return `@@KATEX_BLOCK_${idx}@@`
  })

  // Markdown 渲染
  const html = marked.parse(protected_)
  const cleanHtml = DOMPurify.sanitize(html, {
    ADD_TAGS: ['span', 'div'],
    ADD_ATTR: ['class', 'style', 'data-formula']
  })

  // 还原 KaTeX 公式
  let finalHtml = cleanHtml
  placeholders.forEach((p, idx) => {
    try {
      const rendered = katex.renderToString(p.formula, {
        throwOnError: false,
        displayMode: p.type === 'block',
        output: 'html'
      })
      const marker = p.type === 'inline' ? `@@KATEX_INLINE_${idx}@@` : `@@KATEX_BLOCK_${idx}@@`
      if (p.type === 'inline') {
        finalHtml = finalHtml.replace(marker, `<span class="katex-inline">${rendered}</span>`)
      } else {
        finalHtml = finalHtml.replace(marker, `<div class="katex-block my-3">${rendered}</div>`)
      }
    } catch (e) {
      const marker = p.type === 'inline' ? `@@KATEX_INLINE_${idx}@@` : `@@KATEX_BLOCK_${idx}@@`
      finalHtml = finalHtml.replace(marker, `<code class="text-red-500">${p.formula}</code>`)
    }
  })

  return finalHtml
}

// 提取所有公式（用于"公式解析"专用卡片）
function extractFormulas(text) {
  if (!text) return []
  const formulas = []
  // \[...\]
  let m
  const blockRe = /\\\[([\s\S]+?)\\\]/g
  while ((m = blockRe.exec(text)) !== null) {
    formulas.push({ type: 'block', formula: m[1].trim() })
  }
  // $$...$$
  const blockRe2 = /\$\$([\s\S]+?)\$\$/g
  while ((m = blockRe2.exec(text)) !== null) {
    if (!formulas.some(f => f.formula === m[1].trim())) {
      formulas.push({ type: 'block', formula: m[1].trim() })
    }
  }
  // \(...\)
  const inlineRe = /\\\(([\s\S]+?)\\\)/g
  while ((m = inlineRe.exec(text)) !== null) {
    if (!formulas.some(f => f.formula === m[1].trim())) {
      formulas.push({ type: 'inline', formula: m[1].trim() })
    }
  }
  // $...$
  const inlineRe2 = /(?<![\\$])\$([^\$\n]+?)\$(?![\\$])/g
  while ((m = inlineRe2.exec(text)) !== null) {
    if (!formulas.some(f => f.formula === m[1].trim())) {
      formulas.push({ type: 'inline', formula: m[1].trim() })
    }
  }
  return formulas
}

// 渲染单个公式（纯 KaTeX，无 Markdown）
function renderKatex(formula, displayMode = false) {
  try {
    return katex.renderToString(formula, {
      throwOnError: false,
      displayMode,
      output: 'html'
    })
  } catch (e) {
    return `<code class="text-red-500">${formula}</code>`
  }
}

// 进度条颜色
const progressColors = {
  pending: 'bg-gray-300',
  processing: 'bg-blue-500',
  completed: 'bg-green-500',
  failed: 'bg-red-500'
}

function AnalyzePage() {
  const fileInputRef = useRef(null)
  const [step, setStep] = useState('upload')
  const [mode, setMode] = useState('file')

  const [file, setFile] = useState(null)
  const [fileContent, setFileContent] = useState('')
  const [pasteText, setPasteText] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const [taskId, setTaskId] = useState(null)
  const [taskStatus, setTaskStatus] = useState('')
  const [taskProgress, setTaskProgress] = useState(0)
  const [taskError, setTaskError] = useState('')

  const [result, setResult] = useState(null)
  const [activeVizIndex, setActiveVizIndex] = useState(0)
  const [copiedSection, setCopiedSection] = useState('')

  const [expandedSections, setExpandedSections] = useState({
    abstract: true,
    full_translation: true,
    summary: true,
    formulas: true,
    visualizations: true,
    significance: true
  })

  // 提取公式和预渲染内容
  const formulaList = useMemo(() => {
    if (!result?.formula_explanations && !result?.full_translation) return []
    const fromExplanations = (result?.formula_explanations || []).map(f => ({
      ...f,
      type: f.type || 'block'
    }))
    if (fromExplanations.length > 0) return fromExplanations
    return extractFormulas(result?.full_translation || '')
  }, [result])

  const translatedHtml = useMemo(() => {
    if (!result?.full_translation) return ''
    return renderMarkdownWithKatex(result.full_translation)
  }, [result])

  const abstractHtml = useMemo(() => {
    if (!result?.abstract_cn) return ''
    return renderMarkdownWithKatex(result.abstract_cn)
  }, [result])

  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return
    const ext = selectedFile.name.split('.').pop().toLowerCase()
    if (!['pdf', 'docx', 'doc', 'txt'].includes(ext)) {
      setUploadError('仅支持 PDF、Word（.docx/.doc）或 TXT 格式文件')
      return
    }
    setUploadError('')
    setFile(selectedFile)

    if (ext === 'txt') {
      const reader = new FileReader()
      reader.onload = (e) => setFileContent(e.target.result)
      reader.onerror = () => setUploadError('文件读取失败')
      reader.readAsText(selectedFile)
      return
    }

    if (ext === 'pdf' || ext === 'docx' || ext === 'doc') {
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target.result
        if (text && text.length > 100) {
          const lang = detectLanguage(text)
          const fileName = selectedFile ? selectedFile.name : 'pasted-text.txt'
          const fileType = selectedFile ? selectedFile.name.split('.').pop().toLowerCase() : 'txt'
          setFileContent(text)
          setPasteText(text)
          setUploading(true)
          setUploadError('')
          submitAnalysis(text, lang, fileName, fileType)
        } else {
          setUploadError('文件已读取，请在下方文本框中粘贴论文内容')
        }
      }
      reader.onerror = () => setUploadError('文件读取失败，请直接粘贴论文内容')
      try { reader.readAsText(selectedFile) } catch { /* ignore */ }
    }
  }, [])

  const submitAnalysis = async (text, lang, fileName, fileType) => {
    setStep('processing')
    setUploadError('')

    try {
      const submitRes = await fetch(`${API_BASE}/api/analyze/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_name: fileName,
          file_type: fileType,
          file_data: text
        })
      })
      const submitData = await submitRes.json()
      if (!submitData.success) throw new Error(submitData.error || '提交失败')

      const newTaskId = submitData.id
      setTaskId(newTaskId)

      const runRes = await fetch(`${API_BASE}/api/analyze/run/${newTaskId}`, {
        method: 'POST'
      })
      const runData = await runRes.json()

      if (!runData.success) {
        throw new Error(runData.error || '分析失败')
      } else {
        setResult(runData.result)
        setStep('result')
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } catch (e) {
      setUploadError(e.message || '提交失败，请重试')
      setStep('upload')
    }

    setUploading(false)
  }

  const handleSubmit = async () => {
    const text = (fileContent || pasteText).trim()
    if (!text) {
      setUploadError('请先上传文件或粘贴论文内容')
      return
    }
    if (text.length < 100) {
      setUploadError('论文内容过短，请确保粘贴了完整的论文内容')
      return
    }

    const lang = detectLanguage(text)
    const fileName = file ? file.name : 'pasted-text.txt'
    const fileType = file ? file.name.split('.').pop().toLowerCase() : 'txt'

    setUploading(true)
    setUploadError('')
    await submitAnalysis(text, lang, fileName, fileType)
  }

  useEffect(() => {
    if (!taskId || step !== 'processing') return
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/analyze/status/${taskId}`)
        const data = await res.json()
        setTaskStatus(data.status || '')
        setTaskProgress(data.progress || 0)
        if (data.status === 'completed' && data.result_json) {
          setResult(data.result_json)
          setStep('result')
          clearInterval(interval)
        } else if (data.status === 'failed') {
          setTaskError(data.error_message || '分析失败')
          clearInterval(interval)
        }
      } catch { /* ignore */ }
    }, 3000)
    return () => clearInterval(interval)
  }, [taskId, step])

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSection(key)
      setTimeout(() => setCopiedSection(''), 2000)
    })
  }

  const progressSteps = [
    { label: '文件解析', value: 15 },
    { label: '内容理解', value: 35 },
    { label: '全文翻译', value: 60 },
    { label: '深度总结', value: 80 },
    { label: '公式解析', value: 90 },
    { label: '可视化生成', value: 100 }
  ]
  const currentStep = progressSteps.find(s => s.value >= taskProgress) || progressSteps[0]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-primary-700 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">返回首页</span>
          </Link>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <span className="hidden sm:inline">文献智能分析</span>
            <span className="sm:hidden">分析</span>
          </h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {step !== 'result' && (
          <div className="flex items-center justify-center mb-8 sm:mb-10 overflow-x-auto">
            {[
              { icon: Upload, label: '上传', active: step === 'upload' },
              { icon: Loader2, label: '分析', active: step === 'processing' },
              { icon: Eye, label: '结果', active: step === 'result' }
            ].map((item, idx) => {
              const Icon = item.icon
              return (
                <div key={idx} className="flex items-center flex-shrink-0">
                  <div className={`flex flex-col items-center gap-1 ${item.active ? 'opacity-100' : 'opacity-40'}`}>
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
                      item.active ? 'bg-primary-600 text-white shadow-lg' : 'bg-gray-200 text-gray-500'
                    }`}>
                      <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${item.active ? 'animate-spin' : ''}`} />
                    </div>
                    <span className={`text-xs font-medium ${item.active ? 'text-primary-700' : 'text-gray-500'}`}>
                      {item.label}
                    </span>
                  </div>
                  {idx < 2 && (
                    <div className={`w-12 sm:w-16 h-0.5 mx-1 sm:mx-2 ${step === 'result' ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              )
            })}
          </div>
        )}

        {step === 'upload' && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md flex-shrink-0">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">AI 智能文献分析</h2>
                  <p className="text-gray-500 text-xs sm:text-sm">上传 PDF / Word，一键完成全文逐字翻译、公式解析、深度总结</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: FileText, title: '逐字翻译', desc: '外文→中文' },
                  { icon: Sigma, title: '公式解析', desc: 'LaTeX 渲染' },
                  { icon: Microscope, title: '深度总结', desc: '逐项详细' },
                  { icon: BarChart3, title: '图表生成', desc: '智能可视化' }
                ].map((f, i) => {
                  const Icon = f.icon
                  return (
                    <div key={i} className="bg-blue-50 rounded-xl p-3 sm:p-4 text-center">
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 mx-auto mb-2" />
                      <div className="font-semibold text-gray-900 text-xs sm:text-sm">{f.title}</div>
                      <div className="text-xs text-gray-500 mt-1 hidden sm:block">{f.desc}</div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setMode('file')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'file' ? 'bg-primary-600 text-white shadow' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Upload className="w-4 h-4" />上传文件
              </button>
              <button
                onClick={() => setMode('text')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'text' ? 'bg-primary-600 text-white shadow' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <FileText className="w-4 h-4" />粘贴文本
              </button>
            </div>

            {mode === 'file' ? (
              <div
                className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-8 sm:p-12 text-center hover:border-primary-400 transition-all cursor-pointer group"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary-400', 'bg-primary-50') }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50') }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50')
                  const f = e.dataTransfer.files[0]
                  if (f) handleFileSelect(f)
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                />
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary-50 transition-colors">
                  <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-primary-500" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                  拖拽文件到此处，或点击选择
                </h3>
                <p className="text-gray-500 text-xs sm:text-sm mb-4">支持 PDF、Word（.docx / .doc）、TXT 格式</p>
                <div className="flex justify-center gap-2 flex-wrap">
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">PDF</span>
                  <span className="px-3 py-1 bg-green-50 text-green-700 text-xs rounded-full">Word</span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">TXT</span>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base">粘贴论文内容</h3>
                </div>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="请将论文内容粘贴到此处..."
                  className="w-full h-64 sm:h-80 p-4 border border-gray-200 rounded-xl text-sm text-gray-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <div className="flex justify-between items-center mt-3">
                  <span className="text-xs text-gray-400">
                    {pasteText.length > 0 ? `已输入 ${pasteText.length} 字` : ''}
                    {detectLanguage(pasteText) === 'zh' && pasteText.length > 0 && (
                      <span className="ml-2 text-green-600 font-medium">✓ 检测为中文</span>
                    )}
                    {detectLanguage(pasteText) === 'en' && pasteText.length > 50 && (
                      <span className="ml-2 text-blue-600 font-medium">✓ 检测为英文，将逐字翻译</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {uploadError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {uploadError}
              </div>
            )}

            <div className="mt-6 flex justify-center">
              <button
                onClick={handleSubmit}
                disabled={uploading || ((!fileContent || !fileContent.trim()) && (!pasteText || !pasteText.trim()))}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
              >
                {uploading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" />提交中...</>
                ) : (
                  <><Zap className="w-5 h-5" />开始智能分析</>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 sm:p-10 text-center">
              <div className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-6 sm:mb-8">
                <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
                <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" style={{ animationDuration: '1.5s' }} />
                <div className="absolute inset-4 rounded-full border-4 border-indigo-400 border-b-transparent animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-primary-600" />
                </div>
              </div>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">AI 正在深度分析...</h2>
              <p className="text-gray-500 text-sm mb-6 sm:mb-8">逐字翻译 + 公式解析 + 内容总结，通常需要 30-60 秒</p>

              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span className="font-medium text-primary-700">{currentStep.label}</span>
                  <span>{taskProgress}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-primary-600 rounded-full transition-all duration-500"
                    style={{ width: `${taskProgress}%` }}
                  />
                </div>
              </div>

              <div className="space-y-2 text-left bg-gray-50 rounded-xl p-4">
                {progressSteps.map((s) => (
                  <div key={s.label} className="flex items-center gap-3 text-sm">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      taskProgress >= s.value ? 'bg-primary-500' : 'bg-gray-300'
                    }`} />
                    <span className={taskProgress >= s.value ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                      {s.label}
                    </span>
                    {taskProgress >= s.value && <CheckCircle className="w-3 h-3 text-primary-500 ml-auto" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-5 sm:space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 flex-wrap">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <div className="text-green-800 font-medium">分析完成</div>
                <div className="text-green-600 text-xs sm:text-sm">完整翻译 + 公式解析 + 深度总结</div>
              </div>
              <button onClick={() => { setStep('upload'); setResult(null); setFile(null); setFileContent(''); setPasteText('') }}
                className="ml-auto px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors">
                分析新文献
              </button>
            </div>

            {/* 论文信息卡片 */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-primary-500 to-blue-600 flex items-center justify-center shadow flex-shrink-0">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">{result.title || result.title_cn}</h2>
                  {result.title && result.title_cn && result.title !== result.title_cn && (
                    <p className="text-gray-500 text-xs sm:text-sm mt-1 italic">{result.title}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: '作者', value: result.authors },
                  { label: '期刊', value: result.journal },
                  { label: '年份', value: result.year },
                  { label: '分类', value: result.category }
                ].map(item => item.value && (
                  <div key={item.label} className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-400 mb-1">{item.label}</div>
                    <div className="text-sm font-medium text-gray-900 truncate">{item.value}</div>
                  </div>
                ))}
              </div>
              {result.keywords && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {result.keywords.split(/[,，;；]/).filter(Boolean).map((k, i) => (
                    <span key={i} className="px-2.5 py-1 bg-primary-50 text-primary-700 text-xs rounded-full font-medium">
                      {k.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 中文摘要 */}
            {result.abstract_cn && (
              <ResultCard
                icon={FileText}
                iconBg="from-amber-500 to-orange-600"
                title="中文摘要"
                subtitle="论文核心内容概述"
                expanded={expandedSections.abstract}
                onToggle={() => toggleSection('abstract')}
              >
                <div
                  className="prose prose-sm sm:prose-base max-w-none text-gray-700 leading-relaxed
                    [&_p]:my-2 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2
                    [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-2
                    [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1
                    [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1
                    [&_strong]:text-gray-900 [&_em]:text-gray-600
                    [&_blockquote]:border-l-4 [&_blockquote]:border-amber-300 [&_blockquote]:bg-amber-50 [&_blockquote]:py-2 [&_blockquote]:px-3 [&_blockquote]:my-2"
                  dangerouslySetInnerHTML={{ __html: abstractHtml }}
                />
              </ResultCard>
            )}

            {/* 深度总结 */}
            {result.summary && (
              <ResultCard
                icon={Microscope}
                iconBg="from-purple-500 to-pink-500"
                title="深度内容总结"
                subtitle="逐项详细解读论文核心要素"
                expanded={expandedSections.summary}
                onToggle={() => toggleSection('summary')}
              >
                <div className="space-y-5">
                  {[
                    { icon: Target, label: '研究目的与背景', key: 'research_objective', color: 'from-blue-500 to-blue-600' },
                    { icon: Beaker, label: '研究方法', key: 'research_methods', color: 'from-green-500 to-emerald-500' },
                    { icon: Activity, label: '主要结果与发现', key: 'main_results', color: 'from-orange-500 to-red-500' },
                    { icon: Lightbulb, label: '研究意义', key: 'significance', color: 'from-purple-500 to-indigo-500' },
                    { icon: AlertCircle, label: '研究局限性', key: 'limitations', color: 'from-gray-500 to-gray-600' },
                    { icon: Sparkles, label: '未来研究方向', key: 'future_work', color: 'from-cyan-500 to-blue-500' }
                  ].map((item) => {
                    const Icon = item.icon
                    const content = result.summary[item.key]
                    if (!content) return null
                    return (
                      <div key={item.key} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-5 border border-gray-100">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-sm flex-shrink-0`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <h4 className="font-bold text-gray-900 text-sm sm:text-base">{item.label}</h4>
                        </div>
                        {Array.isArray(content) ? (
                          <div className="text-gray-700 text-sm leading-relaxed space-y-2">
                            {content.map((c, i) => (
                              <p key={i} className="flex items-start gap-2">
                                <span className="text-primary-500 mt-1 flex-shrink-0">▸</span>
                                <span>{c}</span>
                              </p>
                            ))}
                          </div>
                        ) : (
                          <div
                            className="text-gray-700 text-sm leading-relaxed
                              [&_p]:my-2 [&_strong]:text-gray-900
                              [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownWithKatex(content) }}
                          />
                        )}
                      </div>
                    )
                  })}

                  {result.summary.key_findings && result.summary.key_findings.length > 0 && (
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 sm:p-5 border border-amber-100">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center shadow-sm">
                          <Zap className="w-4 h-4 text-white" />
                        </div>
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base">核心发现</h4>
                      </div>
                      <div className="space-y-3">
                        {result.summary.key_findings.map((f, i) => (
                          <div key={i} className="flex items-start gap-3 bg-white/60 rounded-lg p-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                            <div
                              className="text-gray-700 text-sm leading-relaxed flex-1
                                [&_p]:my-1 [&_strong]:text-gray-900"
                              dangerouslySetInnerHTML={{ __html: renderMarkdownWithKatex(f) }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ResultCard>
            )}

            {/* 公式解析（重点新增） */}
            {formulaList.length > 0 && (
              <ResultCard
                icon={Sigma}
                iconBg="from-indigo-500 to-purple-600"
                title={`公式解析（共 ${formulaList.length} 个）`}
                subtitle="使用 LaTeX 渲染，逐条解释每个物理量与每一项意义"
                expanded={expandedSections.formulas}
                onToggle={() => toggleSection('formulas')}
              >
                <div className="space-y-4">
                  {formulaList.map((f, idx) => {
                    const explanation = typeof f === 'object' && f.explanation ? f.explanation : null
                    const formulaStr = typeof f === 'string' ? f : f.formula
                    const isBlock = (typeof f === 'object' && f.type === 'block') || /\n/.test(formulaStr) || formulaStr.length > 50
                    return (
                      <div key={idx} className="bg-gradient-to-br from-indigo-50/50 to-white rounded-xl p-4 sm:p-5 border border-indigo-100">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="text-sm font-bold text-gray-900">公式 {idx + 1}</span>
                          <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${isBlock ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>
                            {isBlock ? '块级公式' : '行内公式'}
                          </span>
                        </div>
                        <div className="bg-white rounded-lg p-4 my-3 overflow-x-auto border border-gray-100">
                          <div dangerouslySetInnerHTML={{ __html: renderKatex(formulaStr, isBlock) }} />
                        </div>
                        {explanation && (
                          <div
                            className="text-gray-700 text-sm leading-relaxed mt-3
                              [&_p]:my-2 [&_strong]:text-indigo-900 [&_em]:text-gray-600
                              [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1
                              [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-300 [&_blockquote]:bg-indigo-50 [&_blockquote]:py-2 [&_blockquote]:px-3 [&_blockquote]:my-2"
                            dangerouslySetInnerHTML={{ __html: renderMarkdownWithKatex(explanation) }}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </ResultCard>
            )}

            {/* 全文翻译 */}
            {result.full_translation && (
              <ResultCard
                icon={Globe}
                iconBg="from-emerald-500 to-teal-600"
                title="全文翻译"
                subtitle="按章节逐字翻译（中文原文保留）"
                expanded={expandedSections.full_translation}
                onToggle={() => toggleSection('full_translation')}
                extra={
                  <button
                    onClick={(e) => { e.stopPropagation(); copyText(result.full_translation, 'translation') }}
                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                    title="复制翻译"
                  >
                    {copiedSection === 'translation' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                }
              >
                <div
                  className="prose prose-sm sm:prose-base max-w-none text-gray-700 leading-loose
                    [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:pb-2 [&_h1]:border-b [&_h1]:border-emerald-200
                    [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-emerald-800
                    [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2
                    [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1
                    [&_p]:my-3 [&_p]:leading-loose
                    [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1.5
                    [&_strong]:text-emerald-900 [&_em]:text-gray-600
                    [&_blockquote]:border-l-4 [&_blockquote]:border-emerald-300 [&_blockquote]:bg-emerald-50 [&_blockquote]:py-2 [&_blockquote]:px-4 [&_blockquote]:my-3
                    [&_code]:bg-gray-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
                    [&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto
                    [&_a]:text-emerald-600 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: translatedHtml }}
                />
              </ResultCard>
            )}

            {/* 可视化图表 */}
            {result.visualizations && result.visualizations.length > 0 && (
              <ResultCard
                icon={BarChart3}
                iconBg="from-cyan-500 to-blue-600"
                title="智能可视化图表"
                subtitle={`基于论文内容自动生成 ${result.visualizations.length} 张数据图表`}
                expanded={expandedSections.visualizations}
                onToggle={() => toggleSection('visualizations')}
              >
                <div className="space-y-5">
                  <div className="flex flex-wrap gap-2">
                    {result.visualizations.map((viz, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveVizIndex(idx)}
                        className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                          activeVizIndex === idx
                            ? 'bg-primary-600 text-white shadow'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        图表 {idx + 1}
                      </button>
                    ))}
                  </div>

                  {result.visualizations[activeVizIndex] && (
                    <div className="border border-gray-100 rounded-xl overflow-hidden">
                      <div className="bg-gradient-to-r from-gray-50 to-cyan-50 px-4 sm:px-5 py-4 border-b border-gray-100">
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base">{result.visualizations[activeVizIndex].title}</h4>
                        {result.visualizations[activeVizIndex].description && (
                          <p className="text-gray-600 text-xs sm:text-sm mt-1.5 leading-relaxed">{result.visualizations[activeVizIndex].description}</p>
                        )}
                      </div>
                      <div className="p-3 sm:p-4 bg-white">
                        <ReactECharts
                          option={result.visualizations[activeVizIndex].chart_config || {}}
                          style={{ height: 360 }}
                          opts={{ renderer: 'canvas' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ResultCard>
            )}

            {/* 气象学意义 */}
            {result.meteorological_significance && (
              <ResultCard
                icon={Wind}
                iconBg="from-rose-500 to-pink-600"
                title="气象学意义与应用价值"
                subtitle="研究对气象学科的贡献与实际应用"
                expanded={expandedSections.significance}
                onToggle={() => toggleSection('significance')}
              >
                <div
                  className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-5 sm:p-6 border border-rose-100 text-gray-800 leading-loose
                    [&_p]:my-3 [&_strong]:text-rose-900
                    [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1.5"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownWithKatex(result.meteorological_significance) }}
                />
              </ResultCard>
            )}

            {/* 底部推荐 */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 mt-8">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Quote className="w-5 h-5 text-primary-600" />
                还想做什么？
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link to="/visualizations" className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl hover:from-blue-100 hover:to-cyan-100 transition-all border border-blue-100">
                  <BarChart3 className="w-6 h-6 text-blue-600 mb-2" />
                  <div className="font-semibold text-gray-900 text-sm">可视化中心</div>
                  <div className="text-xs text-gray-500 mt-1">浏览所有数据图表</div>
                </Link>
                <button onClick={() => { setStep('upload'); setResult(null); setFile(null); setFileContent(''); setPasteText('') }}
                  className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-all border border-amber-100 text-left">
                  <FileCheck className="w-6 h-6 text-amber-600 mb-2" />
                  <div className="font-semibold text-gray-900 text-sm">分析下一篇</div>
                  <div className="text-xs text-gray-500 mt-1">继续分析其他文献</div>
                </button>
                <Link to="/" className="p-4 bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl hover:from-slate-100 hover:to-gray-100 transition-all border border-gray-200">
                  <Layers className="w-6 h-6 text-gray-600 mb-2" />
                  <div className="font-semibold text-gray-900 text-sm">返回首页</div>
                  <div className="text-xs text-gray-500 mt-1">浏览收录文献</div>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// 可复用的结果卡片组件
function ResultCard({ icon: Icon, iconBg, title, subtitle, expanded, onToggle, extra, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-5 sm:p-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br ${iconBg} flex items-center justify-center shadow flex-shrink-0`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{title}</h3>
            {subtitle && <p className="text-gray-500 text-xs sm:text-sm truncate">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {extra}
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>
      {expanded && <div className="px-5 sm:px-6 pb-5 sm:pb-6">{children}</div>}
    </div>
  )
}

export default AnalyzePage